// RECALL-RULES on a DE's Overview: the standing rules that bind it. Its own rules first, then the
// company's (collapsed: "N company rules"), then the board automations it owns (B1-DE-RULES) as
// links to Work › Automations. A visible "+" adds a rule for this DE (scope prefilled, source
// `de_page`); owners and admins lift an active rule from its hover icon.
import { Button, WorkerBadge, workerName } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDownIcon, ChevronRightIcon, ZapIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { AddRuleButton, memoryRulesQuery, RuleRow, useRuleModals } from "../memory/rules";
import { LoadError } from "../screen-parts";
import { Empty, Part } from "./de-about";

export function DeRules({ api, company, de }: { api: Api; company: CompanyAccess; de: DeProfile }) {
  const co = company.id;
  const q = useQuery(memoryRulesQuery(api, co, de.membership_id));
  const canManage = q.data?.can_manage;
  const modals = useRuleModals(api, co, canManage);
  const [open, setOpen] = useState(false);
  const name = workerName(de.display_name);
  const own = (q.data?.data ?? []).filter((r) => r.scope === "de");
  const shared = (q.data?.data ?? []).filter((r) => r.scope === "company");
  const automations = q.data?.automation_rules ?? [];
  const lift = canManage === true;
  return (
    <Part
      title="Rules"
      slot="de-rules"
      info="What it must always or never do. Told to it at every wake-up."
      actions={
        <AddRuleButton
          label={`Add a rule for ${name}`}
          onClick={() => modals.add({ scope: "de", deId: de.membership_id, source: "de_page" })}
        />
      }
    >
      {modals.dialogs}
      {q.isPending ? (
        <Empty>…</Empty>
      ) : q.isError ? (
        <LoadError what="Rules" error={q.error} />
      ) : (
        <div className="flex min-w-0 flex-col gap-2">
          {own.length ? (
            <ul className="flex flex-col" aria-label={`${name}'s rules`}>
              {own.map((r) => (
                <RuleRow
                  key={r.id}
                  company={company}
                  rule={r}
                  canLift={lift}
                  onLift={modals.lift}
                />
              ))}
            </ul>
          ) : (
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              No rules of its own for {name}
              <WorkerBadge kind="de" size="sm" />
            </span>
          )}
          {shared.length ? (
            <div className="flex flex-col" data-slot="de-company-rules">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 self-start px-1 text-xs text-muted-foreground"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
              >
                {open ? (
                  <ChevronDownIcon aria-hidden="true" />
                ) : (
                  <ChevronRightIcon aria-hidden="true" />
                )}
                {shared.length === 1 ? "1 company rule" : `${shared.length} company rules`}
              </Button>
              {open ? (
                <ul className="flex flex-col pl-5" aria-label="Company rules">
                  {shared.map((r) => (
                    <RuleRow
                      key={r.id}
                      company={company}
                      rule={r}
                      canLift={lift}
                      onLift={modals.lift}
                    />
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          {automations.length ? (
            <div className="flex flex-col gap-1" data-slot="de-automation-rules">
              <span className="text-xs text-faint">Board automations it owns</span>
              <ul className="flex flex-col gap-0.5">
                {automations.map((a) => (
                  <li key={a.id}>
                    <Link
                      to="/c/$slug/work/automations"
                      params={{ slug: company.slug }}
                      search={(prev: Record<string, unknown>) => ({
                        ...keepFocus(prev),
                        board: a.board_id,
                      })}
                      className="inline-flex min-w-0 items-center gap-1.5 text-sm text-foreground underline-offset-4 hover:underline"
                    >
                      <ZapIcon aria-hidden="true" className="size-3.5 text-muted-foreground" />
                      <span className="truncate">{a.name}</span>
                      <span className="truncate text-xs text-faint">
                        {a.board_name}
                        {a.enabled ? "" : " · off"}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      )}
    </Part>
  );
}
